import React, { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { theme } from '../../theme';
import { CATEGORIES, AREAS } from '../../constants';
import { useApi } from '../../hooks/useApi';

const AVATAR_OPTIONS = ['🔧', '🖨️', '⚡', '⚙️', '✂️', '🧵', '🎨', '🪵', '🔩', '💡', '🛠️', '👷'];

export function MakerRegisterScreen({ onNavigate }) {
  const { isAuthenticated, login } = useAuth();
  const api = useApi();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    name: '',
    phone: '',
    bio: '',
    avatar: '🔧',
    location: '',
  });

  if (!isAuthenticated) {
    return (
      <div style={{ padding: '16px', textAlign: 'center', marginTop: '40px' }}>
        <p style={{ fontSize: '40px', marginBottom: '16px' }}>🔒</p>
        <p style={{ color: theme.text, fontSize: '16px', fontWeight: 600, marginBottom: '8px' }}>
          Skráðu þig inn til að verða maker
        </p>
        <button
          onClick={() => login()}
          style={{
            marginTop: '12px',
            background: theme.accent,
            color: '#000',
            border: 'none',
            padding: '12px 24px',
            borderRadius: theme.radiusSm,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Innskrá
        </button>
      </div>
    );
  }

  const update = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      setError('Nafn er nauðsynlegt');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await api.makers.register({
        name: form.name.trim(),
        phone: form.phone.trim() || null,
        bio: form.bio.trim() || null,
        avatar: form.avatar,
        location: form.location || null,
      });
      onNavigate('my-page');
    } catch (err) {
      setError(err.message || 'Villa kom upp');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ padding: '16px' }}>
      <button
        onClick={() => onNavigate('my-page')}
        style={{ background: 'none', border: 'none', color: theme.accent, cursor: 'pointer', marginBottom: '16px' }}
      >
        ← Til baka
      </button>

      <h2 style={{ fontSize: '18px', fontWeight: 700, color: theme.text, marginBottom: '4px' }}>
        Verða maker
      </h2>
      <p style={{ fontSize: '13px', color: theme.textSecondary, marginBottom: '20px' }}>
        Skráðu þig sem framleiðandi og byrjaðu að taka við verkefnum
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {/* Avatar */}
        <div>
          <label style={labelStyle}>Velja merki</label>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {AVATAR_OPTIONS.map(emoji => (
              <button
                key={emoji}
                onClick={() => update('avatar', emoji)}
                style={{
                  width: '48px',
                  height: '48px',
                  fontSize: '24px',
                  borderRadius: theme.radiusSm,
                  border: `2px solid ${form.avatar === emoji ? theme.accent : theme.border}`,
                  background: form.avatar === emoji ? theme.accentBg : theme.bgCard,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>

        {/* Name */}
        <div>
          <label style={labelStyle}>Nafn / fyrirtæki *</label>
          <input
            value={form.name}
            onChange={e => update('name', e.target.value)}
            placeholder="T.d. Jón 3D Print, MakerLab Akureyri..."
            style={inputStyle}
          />
        </div>

        {/* Phone */}
        <div>
          <label style={labelStyle}>Sími (valfrjálst)</label>
          <input
            value={form.phone}
            onChange={e => update('phone', e.target.value)}
            placeholder="+354 XXX-XXXX"
            type="tel"
            style={inputStyle}
          />
        </div>

        {/* Location */}
        <div>
          <label style={labelStyle}>Staðsetning</label>
          <select
            value={form.location}
            onChange={e => update('location', e.target.value)}
            style={inputStyle}
          >
            <option value="">Veldu svæði</option>
            {AREAS.map(a => (
              <option key={a.id} value={a.label}>{a.label}</option>
            ))}
          </select>
        </div>

        {/* Bio */}
        <div>
          <label style={labelStyle}>Um mig / fyrirtækið (valfrjálst)</label>
          <textarea
            value={form.bio}
            onChange={e => update('bio', e.target.value)}
            placeholder="Segðu frá reynslu þinni, búnaði, sérhæfingu..."
            rows={3}
            style={{ ...inputStyle, resize: 'vertical' }}
          />
        </div>

        {error && (
          <div style={{
            background: `${theme.error}15`,
            border: `1px solid ${theme.error}`,
            borderRadius: theme.radiusSm,
            padding: '10px 14px',
            color: theme.error,
            fontSize: '13px',
          }}>
            {error}
          </div>
        )}

        <button
          onClick={handleSubmit}
          disabled={submitting || !form.name.trim()}
          style={{
            padding: '14px',
            background: theme.accent,
            color: '#000',
            border: 'none',
            borderRadius: theme.radiusSm,
            fontWeight: 700,
            fontSize: '15px',
            cursor: submitting ? 'wait' : 'pointer',
            opacity: submitting || !form.name.trim() ? 0.6 : 1,
          }}
        >
          {submitting ? 'Skrái...' : 'Skrá sem maker'}
        </button>
      </div>
    </div>
  );
}

const labelStyle = {
  fontSize: '13px',
  fontWeight: 600,
  color: theme.textSecondary,
  display: 'block',
  marginBottom: '6px',
};

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  background: theme.bgInput,
  border: `1px solid ${theme.border}`,
  borderRadius: theme.radiusSm,
  color: theme.text,
  fontSize: '14px',
  outline: 'none',
};
